import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, type OpcaoDeSelecao } from '@/componentes/Campo';
import { ControleCarregando } from '@/componentes/Esqueleto';

interface PropsDaConcessao {
  /** Verbo + objeto: "Conceder permissão", "Conceder papel". Rotulo do campo e do botao. */
  readonly rotulo: string;
  readonly rotuloVazio: string;
  readonly opcoes: readonly OpcaoDeSelecao[];
  /**
   * As opcoes ainda vem: a frase que o leitor de tela ouve ("Carregando os
   * papéis disponíveis"). O rotulo fica e o controle vira esqueleto (p.32) —
   * a lista vazia dizia "Nenhum disponível" antes de saber.
   */
  readonly carregando?: string;
  /** Pedido anterior em curso: o botao espera. */
  readonly pendente: boolean;
  /** O que a concessao faz, escrito ao lado dela — e nao nas acoes do painel. */
  readonly nota?: string;
  /** Chame `concluido` quando o servidor aceitar: ele limpa a escolha. */
  readonly aoConceder: (valor: string, concluido: () => void) => void;
}

/**
 * Conceder papel ou permissao: o select ESCOLHE, o botao CONCEDE.
 *
 * O select concedia no proprio `change`. Fechado e focado, ele troca de valor
 * a cada seta do teclado, e o Chrome no Windows dispara `change` a cada uma:
 * quem navegava por teclado concedia varios papeis sem querer, sem confirmacao
 * e com a trilha registrando cada um (p.15: acao so com intencao explicita;
 * regra 8, navegacao por teclado). E o desenho que a administracao da
 * plataforma ja usa para conceder papel.
 */
export function Concessao(props: PropsDaConcessao) {
  const [escolha, setEscolha] = useState('');
  return <DesenhoDaConcessao {...props} escolha={escolha} aoEscolher={setEscolha} />;
}

/** O desenho, sem hook: a escolha vem de fora, e o teste le a arvore sem DOM. */
export function DesenhoDaConcessao({
  rotulo,
  rotuloVazio,
  opcoes,
  carregando,
  pendente,
  nota,
  escolha,
  aoEscolher,
  aoConceder,
}: PropsDaConcessao & {
  readonly escolha: string;
  readonly aoEscolher: (valor: string) => void;
}) {
  /* A escolha que saiu da lista — concedida agora, ou por outra aba — nao vale
     mais: o select volta ao rotulo vazio e o botao nao a concede de novo. */
  const valida = opcoes.some((opcao) => opcao.valor === escolha) ? escolha : '';

  return (
    <>
      {carregando ? (
        /* O rotulo do `Campo`, sem o `label`: ainda nao ha controle a nomear. */
        <div className="flex min-w-0 flex-col gap-2">
          <span className="txt-rotulo-campo">{rotulo}</span>
          <ControleCarregando oQue={carregando} />
        </div>
      ) : (
        <CampoSelecao rotulo={rotulo} valor={valida} rotuloVazio={rotuloVazio} opcoes={opcoes} aoMudar={aoEscolher} />
      )}
      {nota ? <p className="txt-apoio text-tinta-secundaria">{nota}</p> : null}
      <Botao
        nivel="secundario"
        disabled={valida === '' || pendente}
        onClick={() => {
          if (valida === '') return;
          aoConceder(valida, () => {
            aoEscolher('');
          });
        }}
      >
        {rotulo}
      </Botao>
    </>
  );
}
